import React, { useState } from "react";
import { Search, Car, Ban, User, Clock } from "lucide-react";
import { Button } from "../shared/Button";
import { panel as api } from "../../api/modulos.service";
import { estadoDe, etiquetaTipoVisitante } from "../../utils/estados";
import { estilos as est, Cabecera, Vacio, Aviso } from "./base";
import { mostrarDia } from "../../utils/fecha";

/**
 * BÚSQUEDA RÁPIDA — un solo campo.
 *
 * El guardia tiene a alguien delante y lo que le alcanzó a leer: un nombre a
 * medias, tres dígitos de una placa, el número de casa que le gritaron desde
 * el carro. Pedirle que elija primero en qué campo busca es pedirle que
 * clasifique la información antes de tenerla.
 *
 * Así que se busca en todo a la vez: nombre, casa, placa, teléfono, código de
 * visitante y tipo. Y lo restringido sale ARRIBA: si la placa que escribió
 * está reportada, eso es lo primero que tiene que ver, antes que cualquier
 * historial.
 */

/* `fecha` es para una MARCA DE TIEMPO —`entryTime`, `occurredAt`,
 * `receivedAt`—, donde la hora de El Salvador es la correcta.
 *
 * `visit.date` NO es una marca de tiempo: es un día de calendario que se
 * guardó desde un `<input type="date">`, y Mongoose interpreta en UTC una
 * fecha sin hora, así que en la base queda la medianoche UTC. Formatearlo en
 * UTC-6 resta seis horas y enseña el día anterior. Para eso está
 * `mostrarDia`, en `utils/fecha.js`.
 *
 * Los dos conviven en este archivo a propósito: aplicar el mismo ayudante a
 * los dos arregla uno y rompe el otro. */
const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "medium", timeStyle: "short" });

export default function Busqueda() {
  const [termino, setTermino] = useState("");
  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  /* El aviso guarda si tiene sentido reintentar. «Escriba al menos dos
     caracteres» no se arregla pulsando otra vez el mismo botón; «no se pudo
     completar la búsqueda» sí, y es justo el caso en que el guardia necesita
     volver a intentarlo sin reescribir lo que ya escribió. */
  const [aviso, setAviso] = useState(null);

  const consultar = async (q) => {
    setAviso(null);
    setBuscando(true);
    try {
      setResultado(await api.buscar(q));
    } catch (err) {
      setAviso({
        texto: err?.response?.data?.message || "No se pudo completar la búsqueda",
        reintentable: true,
      });
      setResultado(null);
    } finally {
      setBuscando(false);
    }
  };

  const buscar = (e) => {
    e?.preventDefault?.();
    const q = termino.trim();
    if (q.length < 2) {
      setAviso({ texto: "Escriba al menos dos caracteres", reintentable: false });
      return;
    }
    consultar(q);
  };

  const visitas = resultado?.visitas || [];
  const vehiculos = resultado?.vehiculos || [];
  const restricciones = resultado?.restricciones || [];
  /* «Nada coincide» sólo cuando de verdad se buscó y no había nada. Con la
     consulta caída, decirle al guardia que la placa no coincide con nada es
     contestarle una pregunta que el sistema no llegó a hacer: una cosa es que
     esa persona no esté registrada y otra que no se pudo averiguar, y la
     segunda pide llamar a alguien. */
  const nada = !aviso && resultado && !visitas.length && !vehiculos.length && !restricciones.length;

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Búsqueda"
        subtitulo="Nombre, casa, placa, teléfono, código o tipo de visitante."
        icono={Search}
      />

      <form onSubmit={buscar} style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <div style={{ ...est.buscador, flex: "1 1 260px" }}>
          <Search size={18} style={est.buscadorIcono} />
          <input
            style={est.buscadorCampo}
            value={termino}
            onChange={(e) => setTermino(e.target.value)}
            placeholder="P123-456, Juan, casa 42, 7777…"
            autoFocus
          />
        </div>
        <Button variant="primary" loading={buscando} onClick={buscar}>
          Buscar
        </Button>
      </form>

      {aviso && (
        <Aviso>
          {aviso.texto}
          {aviso.reintentable && (
            <div style={{ marginTop: "var(--space-2)" }}>
              {/* Reintentar pasa por `buscar`, no por `consultar`: si entre el
                  fallo y el reintento la persona borró el campo, tiene que
                  salir el mismo aviso de siempre y no una consulta vacía. */}
              <Button size="sm" variant="outline" loading={buscando} onClick={() => buscar()}>
                Reintentar
              </Button>
            </div>
          )}
        </Aviso>
      )}

      {/* Lo restringido, primero y en rojo. */}
      {restricciones.length > 0 && (
        <div
          style={{
            ...est.tarjeta,
            borderLeft: "4px solid var(--danger)",
            backgroundColor: "color-mix(in srgb, var(--danger) 8%, var(--bg-elevated))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <Ban size={18} style={{ color: "var(--danger)" }} />
            <h3 style={{ ...est.tarjetaTitulo, color: "var(--danger)" }}>
              {restricciones.length === 1 ? "Coincide con una restricción" : `Coinciden ${restricciones.length} restricciones`}
            </h3>
          </div>
          {restricciones.map((r) => (
            <div key={r._id} style={{ ...est.tarjetaDato, display: "block" }}>
              <strong style={{ color: "var(--text)" }}>{r.name || r.plate}</strong>
              {r.reason ? ` — ${r.reason}` : ""} · {r.severity || "sin nivel"} · {r.status}
            </div>
          ))}
        </div>
      )}

      {resultado?.parcial && (
        <Aviso>
          El término es muy corto para buscar por partes: sólo se buscaron coincidencias exactas. Escriba tres
          caracteres o más.
        </Aviso>
      )}

      {nada && <Vacio>Nada coincide con «{resultado.termino}».</Vacio>}

      {vehiculos.length > 0 && (
        <div>
          <h3 style={{ ...est.titulo, fontSize: "var(--text-base)", marginBottom: "var(--space-3)" }}>
            Vehículos registrados
          </h3>
          <div style={est.rejilla}>
            {vehiculos.map((v) => (
              <div key={v._id} style={est.tarjeta}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                  <Car size={18} style={{ color: "var(--gold)" }} />
                  <h3 style={est.tarjetaTitulo}>{v.plate}</h3>
                </div>
                <div style={est.tarjetaDato}>
                  {[v.brand, v.model, v.color].filter(Boolean).join(" · ") || "Sin descripción"}
                </div>
                <div style={est.tarjetaDato}>
                  Casa {v.houseNumber}
                  {v.polygon ? `, polígono ${v.polygon}` : ""}
                  {v.ownerName ? ` · ${v.ownerName}` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visitas.length > 0 && (
        <div>
          <h3 style={{ ...est.titulo, fontSize: "var(--text-base)", marginBottom: "var(--space-3)" }}>
            Visitas ({visitas.length})
          </h3>
          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            {visitas.map((v) => {
              const e = estadoDe(v.status);
              return (
                <div key={v._id} style={{ ...est.tarjeta, borderLeft: `4px solid ${e.color}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                    <User size={16} style={{ color: "var(--text-tertiary)" }} />
                    <h3 style={est.tarjetaTitulo}>{v.visitorName}</h3>
                    <span style={{ marginLeft: "auto", fontSize: "var(--text-sm)", color: e.color }}>{e.etiqueta}</span>
                  </div>
                  <div style={est.tarjetaDato}>
                    Casa {v.houseNumber}
                    {v.polygon ? `, polígono ${v.polygon}` : ""}
                    {v.visitorType ? ` · ${etiquetaTipoVisitante(v.visitorType)}` : ""}
                  </div>
                  {v.plate && (
                    <div style={est.tarjetaDato}>
                      <Car size={14} /> {v.plate}
                      {[v.vehicleBrand, v.vehicleModel, v.vehicleColor].filter(Boolean).length
                        ? ` · ${[v.vehicleBrand, v.vehicleModel, v.vehicleColor].filter(Boolean).join(" ")}`
                        : ""}
                    </div>
                  )}
                  <div style={est.tarjetaDato}>
                    <Clock size={14} /> {mostrarDia(v.date, { dateStyle: "medium" })}
                    {v.entryTime ? ` · entró ${fecha(v.entryTime)}` : ""}
                    {v.exitTime ? ` · salió ${fecha(v.exitTime)}` : ""}
                  </div>
                  {v.shortCode && (
                    <div style={{ ...est.tarjetaDato, fontFamily: "monospace" }}>Código {v.shortCode}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
